import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { createChat, updateChat } from '@/services/api/chatbot';
import type { ChatbotChat } from '@/types/chatbot';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    subdomainId: number;
    editing: ChatbotChat | null;
}

export default function ChatFormModal({ open, onOpenChange, subdomainId, editing }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const isEdit = !!editing;

    const [path, setPath] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    useEffect(() => {
        if (open) {
            setPath(editing?.path || '/');
            setName(editing?.name || '');
            setDescription(editing?.description || '');
        }
    }, [open, editing]);

    const createMut = useMutation({
        mutationFn: createChat,
        onSuccess: () => {
            toast({ title: 'Chat criado!' });
            queryClient.invalidateQueries({ queryKey: ['chatbot-chats', subdomainId] });
            onOpenChange(false);
        },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao criar',
            description: e.response?.data?.detail || e.message,
        }),
    });

    const updateMut = useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => updateChat(id, data),
        onSuccess: () => {
            toast({ title: 'Chat atualizado!' });
            queryClient.invalidateQueries({ queryKey: ['chatbot-chats', subdomainId] });
            onOpenChange(false);
        },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao atualizar',
            description: e.response?.data?.detail || e.message,
        }),
    });

    const handleSave = () => {
        if (!path || !name) {
            toast({ variant: 'destructive', title: 'Path e nome sao obrigatorios' });
            return;
        }
        const payload = { path: path.startsWith('/') ? path : '/' + path, name, description: description || undefined };
        if (isEdit && editing) updateMut.mutate({ id: editing.id, data: payload });
        else createMut.mutate({ subdomain_id: subdomainId, ...payload });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Editar chat' : 'Novo chat'}</DialogTitle>
                    <DialogDescription>
                        Cada chat tem um path base. Quizzes dentro dele sao sorteados aleatoriamente no render.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div>
                        <Label>Path base *</Label>
                        <Input value={path} onChange={(e) => setPath(e.target.value)}
                            placeholder="/quiz-emprestimo" />
                        <p className="text-xs text-slate-500 mt-1">
                            Sera acessivel em <span className="font-mono">https://&lt;subdominio&gt;{path}</span>
                        </p>
                    </div>

                    <div>
                        <Label>Nome *</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="Quiz de Emprestimo" />
                    </div>

                    <div>
                        <Label>Descricao</Label>
                        <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                            placeholder="Descricao interna deste chat" rows={3} />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                    <Button onClick={handleSave}
                        disabled={createMut.isPending || updateMut.isPending}>
                        {isEdit ? 'Salvar' : 'Criar'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
